import { Activity, DollarSign, FileSearch, Users, Wrench } from "lucide-react";
import * as React from "react";

import { adminApi, type CostStats, type ToolUsage } from "@/api/admin";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthStore } from "@/stores/auth";

export function AdminPage(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const [costStats, setCostStats] = React.useState<CostStats | null>(null);
  const [toolStats, setToolStats] = React.useState<ToolUsage[] | null>(null);
  const [loading, setLoading] = React.useState(true);

  const isAdmin = user?.roles.includes("admin") || user?.roles.includes("auditor");

  React.useEffect(() => {
    if (!isAdmin) return;
    void (async () => {
      setLoading(true);
      try {
        const [c, t] = await Promise.all([
          adminApi.costStats(7),
          adminApi.toolStats(7),
        ]);
        setCostStats(c);
        setToolStats(t);
      } finally {
        setLoading(false);
      }
    })();
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div className="p-8 text-center">
        <p className="text-muted-foreground">
          Bạn không có quyền truy cập trang admin.
        </p>
        <Button variant="link" onClick={() => window.history.back()}>
          ← Quay lại
        </Button>
      </div>
    );
  }

  return (
    <div className="flex-1 max-w-6xl w-full mx-auto p-6 space-y-6 overflow-y-auto">
      <header>
        <h1 className="text-2xl font-semibold">Admin Dashboard</h1>
        <p className="text-sm text-muted-foreground">
          Theo dõi hệ thống · 7 ngày gần nhất
        </p>
      </header>

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KpiCard
          icon={<DollarSign className="size-5" />}
          label="LLM cost (7d)"
          value={
            loading ? null : (
              <>
                ${costStats?.total_usd.toFixed(4) ?? "0"}
                <span className="text-xs font-normal text-muted-foreground ml-1">
                  USD
                </span>
              </>
            )
          }
        />
        <KpiCard
          icon={<Activity className="size-5" />}
          label="Agent runs"
          value={
            loading
              ? null
              : (costStats?.by_day.reduce((s, d) => s + d.runs, 0) ?? 0)
          }
        />
        <KpiCard
          icon={<Wrench className="size-5" />}
          label="Tool calls"
          value={
            loading
              ? null
              : (toolStats?.reduce((s, t) => s + t.total_calls, 0) ?? 0)
          }
        />
        <KpiCard
          icon={<Users className="size-5" />}
          label="Users"
          value={loading ? null : "—"}
        />
      </div>

      {/* Cost by day */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Chi phí LLM theo ngày</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className="h-32 w-full" />
          ) : (
            <CostBars data={costStats?.by_day ?? []} />
          )}
        </CardContent>
      </Card>

      {/* Top tools */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Tool sử dụng nhiều nhất</CardTitle>
        </CardHeader>
        <CardContent className="px-0">
          {loading ? (
            <Skeleton className="h-32 w-full" />
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/30">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                    Tool
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">
                    Tổng gọi
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">
                    Thành công
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">
                    Latency TB
                  </th>
                </tr>
              </thead>
              <tbody>
                {(toolStats ?? []).map((t) => (
                  <tr key={t.tool_name} className="border-b last:border-b-0">
                    <td className="px-3 py-2 font-mono text-xs">{t.tool_name}</td>
                    <td className="px-3 py-2 text-right">{t.total_calls}</td>
                    <td className="px-3 py-2 text-right">
                      {t.success_count}/{t.total_calls} (
                      {((t.success_count / Math.max(t.total_calls, 1)) * 100).toFixed(0)}%)
                    </td>
                    <td className="px-3 py-2 text-right">
                      {t.avg_latency_ms.toFixed(0)}ms
                    </td>
                  </tr>
                ))}
                {(toolStats ?? []).length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-3 py-8 text-center text-muted-foreground"
                    >
                      Chưa có tool call nào.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FileSearch className="size-4" /> Audit log gần đây
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Button
            variant="outline"
            size="sm"
            onClick={() => alert("Audit log viewer sẽ active trong release tiếp theo")}
          >
            Xem audit log
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function KpiCard({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode | null;
}): JSX.Element {
  return (
    <Card>
      <CardContent className="p-4 space-y-1">
        <div className="flex items-center gap-2 text-muted-foreground">
          {icon}
          <span className="text-xs">{label}</span>
        </div>
        <div className="text-2xl font-semibold tabular-nums">
          {value === null ? <Skeleton className="h-7 w-20" /> : value}
        </div>
      </CardContent>
    </Card>
  );
}

function CostBars({ data }: { data: { date: string; cost_usd: number }[] }): JSX.Element {
  if (data.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4 text-center">
        Chưa có dữ liệu.
      </p>
    );
  }
  const max = Math.max(...data.map((d) => d.cost_usd), 0.0001);
  return (
    <div className="space-y-2">
      {data.map((d) => (
        <div key={String(d.date)} className="flex items-center gap-2 text-xs">
          <span className="w-20 text-muted-foreground tabular-nums">
            {String(d.date).slice(5, 10)}
          </span>
          <div className="flex-1 bg-muted rounded h-5 overflow-hidden">
            <div
              className="bg-primary h-full rounded transition-all"
              style={{ width: `${(d.cost_usd / max) * 100}%` }}
            />
          </div>
          <span className="w-24 text-right tabular-nums">
            ${d.cost_usd.toFixed(4)}
          </span>
        </div>
      ))}
    </div>
  );
}
